import React from "react";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "./ui/button";
import { Link } from "react-router-dom";
import { useModals } from "./modals/ModalProvider";
import HeroGraphic from "./HeroGraphic";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.1 } }),
};

export default function Hero() {
  const { openBookConsultation } = useModals();
  return (
    <section id="hero" className="relative pt-40 sm:pt-44 pb-16 sm:pb-24 overflow-hidden">
      <div className="hero-glow" />
      <div className="absolute inset-0 grid-pattern opacity-40 dark:opacity-30" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-center">
          {/* GRAPHIC — moved to LEFT column (was right) */}
          <motion.div
            initial={{ opacity: 0, scale: 0.94 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.9, delay: 0.15 }}
            className="lg:col-span-6 order-2 lg:order-1"
          >
            <HeroGraphic />
          </motion.div>

          {/* COPY — right column */}
          <div className="lg:col-span-6 order-1 lg:order-2">
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={0}>
              <span data-testid="hero-eyebrow" className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/5 px-3 py-1.5 text-xs font-bold uppercase tracking-widest text-primary">
                <Sparkles className="h-3.5 w-3.5" />
                Engineered for Resilience • Secured for Growth
              </span>
            </motion.div>
            <motion.h1
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={1}
              data-testid="hero-headline"
              className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-black leading-[0.98] tracking-tight"
            >
              Protect Your Business. <br />
              <span className="text-primary">Strengthen Compliance.</span>
              <br />
              Build <span className="text-gradient-brand">Digital Resilience.</span>
            </motion.h1>
            <motion.p
              variants={fadeUp}
              initial="hidden"
              animate="visible"
              custom={2}
              className="mt-6 text-base sm:text-lg text-muted-foreground max-w-2xl leading-relaxed"
              data-testid="hero-subheadline"
            >
              Techmate Infosec Solutions is your strategic cybersecurity consulting partner —
              helping enterprises architect secure infrastructure, protect business-critical data
              and achieve regulatory readiness across ISO 27001, DPDP, SOC 2, RBI and SEBI mandates.
            </motion.p>
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={3} className="mt-8 flex flex-wrap items-center gap-3">
              <Button asChild size="lg" className="rounded-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 px-6 shadow-lg shadow-primary/25">
                <Link data-testid="hero-start-navigator-btn" to="/cyber-risk-navigator">
                  Start Cyber Risk Navigator <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                onClick={openBookConsultation}
                variant="outline"
                size="lg"
                data-testid="hero-book-consultation-btn"
                className="rounded-full h-12 px-6 border-primary/25 hover:border-primary text-foreground"
              >
                Book Consultation
              </Button>
            </motion.div>
            <motion.div variants={fadeUp} initial="hidden" animate="visible" custom={4} className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl">
              {[
                { k: "0–100", l: "Cyber Score" },
                { k: "8+", l: "Compliance frameworks" },
                { k: "45m", l: "Consultant readout" },
                { k: "24×7", l: "Managed SOC" },
              ].map((s) => (
                <div key={s.l} className="rounded-2xl border border-border bg-card/60 backdrop-blur px-3.5 py-2.5">
                  <div className="font-display text-xl sm:text-2xl font-black text-primary">{s.k}</div>
                  <div className="text-[11px] font-semibold text-muted-foreground mt-0.5 leading-tight">{s.l}</div>
                </div>
              ))}
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
